/**
 * Cible Skin Cart Drawer
 * Custom cart drawer component with open/close, quantity updates, and remove functionality.
 * Integrates with Shopify's Cart API and Section Rendering API.
 */

class CskCartDrawer extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    // Close buttons (overlay + X button)
    this.querySelectorAll('[data-csk-cart-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    // Quantity buttons
    this.querySelectorAll('[data-csk-quantity-minus]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const line = parseInt(btn.dataset.cskQuantityMinus);
        const display = this.querySelector(`[data-csk-quantity-display="${line}"]`);
        const currentQty = display ? parseInt(display.textContent) : 1;
        this._updateQuantity(line, currentQty - 1);
      });
    });

    this.querySelectorAll('[data-csk-quantity-plus]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const line = parseInt(btn.dataset.cskQuantityPlus);
        const display = this.querySelector(`[data-csk-quantity-display="${line}"]`);
        const currentQty = display ? parseInt(display.textContent) : 1;
        this._updateQuantity(line, currentQty + 1);
      });
    });

    // Remove buttons
    this.querySelectorAll('[data-csk-remove-line]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.cskRemoveLine);
        this._updateQuantity(line, 0);
      });
    });

    // Listen for cart-icon clicks (from the header)
    document.addEventListener('csk:open-cart-drawer', () => this.open());

    // Listen for theme's cart:update event (CartAddEvent / CartUpdateEvent → ThemeEvents.cartUpdate)
    document.addEventListener('cart:update', (e) => {
      const source = e.detail?.data?.source;
      // Horizon product-form.js uses 'product-form-component'; legacy themes used 'product-form'
      const fromProductForm =
        source === 'product-form-component' || source === 'product-form';
      const fromGiftProduct = source === 'gift-product-block';
      if (this.hasAttribute('auto-open') && (fromProductForm || fromGiftProduct)) {
        this.open();
      }

      // If the drawer is already open and the source is a product-form, the add-to-cart
      // came from inside the drawer (rec card quick-add). Delay the section refresh so
      // the ATC button animation has time to play before the DOM is replaced.
      const refreshDelay = fromProductForm && this._isOpen ? 1400 : 0;

      setTimeout(() => this._refreshSection(), refreshDelay);
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen) {
        this.close();
      }
    });
  }

  open() {
    this._isOpen = true;
    this.classList.add('is-open');
    document.body.classList.add('csk-cart-open');

    // Push history state on mobile for back button support
    if (window.innerWidth < 750 && !history.state?.cskCartOpen) {
      history.pushState({ cskCartOpen: true }, '');
      this._popstateHandler = () => {
        if (this._isOpen) this.close(false);
      };
      window.addEventListener('popstate', this._popstateHandler, { once: true });
    }
  }

  close(updateHistory = true) {
    this._isOpen = false;
    this.classList.remove('is-open');
    document.body.classList.remove('csk-cart-open');

    if (updateHistory && history.state?.cskCartOpen) {
      history.back();
    }
  }

  /**
   * Update cart line quantity via Shopify Cart API, then re-render the section.
   */
  async _updateQuantity(line, quantity) {
    // Visual feedback
    const inner = this.querySelector('.csk-cart-drawer__inner');
    if (inner) inner.classList.add('cart-items-disabled');

    try {
      const response = await fetch(window.Shopify?.routes?.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: this._sectionId,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (data.errors) {
        console.error('Cart error:', data.errors);
        if (inner) inner.classList.remove('cart-items-disabled');
        return;
      }

      // Total item count from the API response
      const itemCount = data.item_count ?? (data.items ? data.items.reduce((sum, item) => sum + item.quantity, 0) : 0);

      // Update the section HTML via Section Rendering API response
      if (data.sections && data.sections[this._sectionId]) {
        this._morphSection(data.sections[this._sectionId]);
      } else {
        await this._refreshSection();
      }

      // Update cart icon bubble via cart-icon's own renderCartBubble method
      // (handles count, visually-hidden toggle, sessionStorage — all in one)
      this._updateCartBubble(itemCount);
    } catch (err) {
      console.error('Failed to update cart:', err);
      if (inner) inner.classList.remove('cart-items-disabled');
    }
  }

  /**
   * Fetch fresh section HTML and replace content.
   */
  async _refreshSection() {
    try {
      const url = `${window.location.pathname}?sections=${this._sectionId}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data[this._sectionId]) {
        this._morphSection(data[this._sectionId]);
      }
    } catch (err) {
      console.error('Failed to refresh cart section:', err);
    }
  }

  /**
   * Replace the drawer's inner HTML with new section content, preserving open state.
   */
  _morphSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('csk-cart-drawer');

    if (!newDrawer) return;

    const newPanel = newDrawer.querySelector('.csk-cart-drawer__panel');
    const currentPanel = this.querySelector('.csk-cart-drawer__panel');

    if (newPanel && currentPanel) {
      currentPanel.innerHTML = newPanel.innerHTML;
      // Re-bind event listeners after morph
      this._rebindEvents();
    }
  }

  /**
   * Re-bind all interactive event listeners after DOM update.
   */
  _rebindEvents() {
    this.querySelectorAll('[data-csk-cart-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.querySelectorAll('[data-csk-quantity-minus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.cskQuantityMinus);
        const display = this.querySelector(`[data-csk-quantity-display="${line}"]`);
        const currentQty = display ? parseInt(display.textContent) : 1;
        this._updateQuantity(line, currentQty - 1);
      });
    });

    this.querySelectorAll('[data-csk-quantity-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.cskQuantityPlus);
        const display = this.querySelector(`[data-csk-quantity-display="${line}"]`);
        const currentQty = display ? parseInt(display.textContent) : 1;
        this._updateQuantity(line, currentQty + 1);
      });
    });

    this.querySelectorAll('[data-csk-remove-line]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.cskRemoveLine);
        this._updateQuantity(line, 0);
      });
    });

  }

  /**
   * Update the cart icon bubble by delegating to cart-icon's renderCartBubble.
   * This ensures the count, visually-hidden state, and sessionStorage are all
   * kept in sync by the component that owns them.
   * @param {number} count - The new total item count.
   */
  _updateCartBubble(count) {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') {
        // false = not from product form (set total, don't add delta)
        // false = no bounce animation on removal
        icon.renderCartBubble(count, false, false);
      }
    });
  }
}

if (!customElements.get('csk-cart-drawer')) {
  customElements.define('csk-cart-drawer', CskCartDrawer);
}
